// ========================================
// SophyCacaPalavras - Página do Caça-Palavras
// ========================================
// Versão: 1.0
// Data: 01 de Novembro de 2025

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import CacaPalavras from '../components/CacaPalavras';

interface Grade {
  tema: string;
  area_estudo?: string;
  dificuldade: number;
  tamanho: number;
  grade: string[][];
  palavras: any[];
  total_palavras: number;
}

interface TemasAreas {
  temas: string[];
  areas: string[];
}

export default function SophyCacaPalavras() {
  const navigate = useNavigate();
  
  // Estados de configuração
  const [temasAreas, setTemasAreas] = useState<TemasAreas>({ temas: [], areas: [] });
  const [tema, setTema] = useState('');
  const [areaEstudo, setAreaEstudo] = useState('');
  const [dificuldade, setDificuldade] = useState(5);
  const [tamanho, setTamanho] = useState(15);
  const [totalPalavras, setTotalPalavras] = useState(10);
  
  // Estados de jogo
  const [gradeAtual, setGradeAtual] = useState<Grade | null>(null);
  const [loading, setLoading] = useState(false);
  const [jogoIniciado, setJogoIniciado] = useState(false);
  const [timer, setTimer] = useState(0);
  const [palavrasEncontradas, setPalavrasEncontradas] = useState(0);

  // Carregar temas e áreas disponíveis
  useEffect(() => {
    carregarTemasAreas();
  }, []);

  // Timer
  useEffect(() => {
    if (jogoIniciado) {
      const interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [jogoIniciado]);

  const carregarTemasAreas = async () => {
    try {
      const response = await api.get('/sophygames/temas-areas');
      
      if (response.data.sucesso) {
        setTemasAreas(response.data.dados);
      }
    } catch (error: any) {
      console.error('[CACA-PALAVRAS] Erro ao carregar temas/áreas:', error);
    }
  };

  const gerarJogo = async () => {
    setLoading(true);
    try {
      console.log('[CACA-PALAVRAS] Gerando caça-palavras com configuração:', {
        tema: tema || 'Todos',
        dificuldade,
        tamanho,
        totalPalavras
      });
      
      const response = await api.post('/sophygames/cacapalavras/gerar', {
        tema: tema || undefined,
        area_estudo: areaEstudo || undefined,
        dificuldade,
        tamanho,
        totalPalavras,
        minCaracteres: 3,
        maxCaracteres: 12
      });
      
      if (response.data.sucesso) {
        const grade = response.data.dados;
        setGradeAtual(grade);
        setJogoIniciado(true);
        setTimer(0);
        setPalavrasEncontradas(0);
        
        console.log('[CACA-PALAVRAS] Jogo gerado com sucesso:', {
          tema: grade.tema,
          tamanho: grade.tamanho,
          totalPalavras: grade.total_palavras
        });
      } else {
        alert('Erro ao gerar caça-palavras: ' + response.data.mensagem);
      }
    } catch (error: any) {
      console.error('[CACA-PALAVRAS] Erro ao gerar jogo:', error);
      alert('Erro ao gerar caça-palavras: ' + (error.response?.data?.mensagem || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePalavraEncontrada = (palavra: string) => {
    setPalavrasEncontradas(prev => prev + 1);
    console.log('[CACA-PALAVRAS] Palavra encontrada:', palavra);
  };

  const handleTodasEncontradas = () => {
    const minutos = Math.floor(timer / 60);
    const segundos = timer % 60;
    
    // Exibir resultado
    const mensagem = `🎉 Parabéns! Você encontrou todas as palavras!\n\n` +
      `⏱️ Tempo: ${minutos}m ${segundos}s\n` +
      `🔤 Palavras: ${gradeAtual?.total_palavras}`;
    
    console.log('[CACA-PALAVRAS] Jogo concluído:', {
      tempo: `${minutos}m ${segundos}s`,
      palavras: gradeAtual?.total_palavras
    });
    
    // Exibir alert e aguardar OK
    alert(mensagem);
    
    // Voltar para tela de configuração após fechar o alert
    console.log('[CACA-PALAVRAS] Retornando para tela de configuração...');
    voltarConfig();
  };

  const voltarConfig = () => {
    console.log('[CACA-PALAVRAS] Voltando para configuração...');
    setJogoIniciado(false);
    setGradeAtual(null);
    setTimer(0);
    setPalavrasEncontradas(0);
  };

  const formatarTempo = (segundos: number) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col-md-8">
          <h2 className="mb-2">
            <i className="fas fa-gamepad mr-2"></i>
            🔤 SophyGames - Caça-Palavras
          </h2>
          <p className="text-muted">
            Encontre termos de Psicologia escondidos na grade de letras
          </p>
        </div>
        <div className="col-md-4 text-right">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate('/sophygames')}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Voltar ao SophyGames
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {!jogoIniciado ? (
            // ========================================
            // Configuração do Jogo
            // ========================================
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">⚙️ Configure seu Caça-Palavras</h3>
              </div>
              <div className="card-body">
                <div className="row">
                  {/* Filtros */}
                  <div className="col-md-6 mb-3">
                    <label htmlFor="cacaPalavrasTema">📚 Tema (opcional)</label>
                    <select
                      className="form-control"
                      id="cacaPalavrasTema"
                      value={tema}
                      onChange={(e) => setTema(e.target.value)}
                    >
                      <option value="">Todos os temas</option>
                      {temasAreas.temas.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="cacaPalavrasAreaEstudo">🎯 Área de Estudo (opcional)</label>
                    <select
                      className="form-control"
                      id="cacaPalavrasAreaEstudo"
                      value={areaEstudo}
                      onChange={(e) => setAreaEstudo(e.target.value)}
                    >
                      <option value="">Todas as áreas</option>
                      {temasAreas.areas.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dificuldade */}
                  <div className="col-md-4 mb-3">
                    <label htmlFor="cacaPalavrasDificuldade">⭐ Dificuldade Máxima: {dificuldade}/10</label>
                    <input
                      type="range"
                      className="custom-range"
                      id="cacaPalavrasDificuldade"
                      min="1"
                      max="10"
                      value={dificuldade}
                      onChange={(e) => setDificuldade(parseInt(e.target.value))}
                    />
                  </div>

                  {/* Tamanho */}
                  <div className="col-md-4 mb-3">
                    <label>📏 Tamanho da Grade: {tamanho}x{tamanho}</label>
                    <select
                      className="form-control"
                      value={tamanho}
                      onChange={(e) => setTamanho(parseInt(e.target.value))}
                    >
                      <option value="10">10x10 (Pequeno)</option>
                      <option value="15">15x15 (Médio)</option>
                      <option value="20">20x20 (Grande)</option>
                    </select>
                  </div>

                  {/* Total de Palavras */}
                  <div className="col-md-4 mb-3">
                    <label>🔢 Quantidade de Palavras: {totalPalavras}</label>
                    <input
                      type="range"
                      className="custom-range"
                      min="5"
                      max="20"
                      value={totalPalavras}
                      onChange={(e) => setTotalPalavras(parseInt(e.target.value))}
                    />
                  </div>
                </div>

                {/* Botão Gerar */}
                <div className="text-center mt-4">
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={gerarJogo}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2"></span>
                        Gerando...
                      </>
                    ) : (
                      <>🎮 Gerar Caça-Palavras</>
                    )}
                  </button>
                </div>

                {/* Informação */}
                <div className="alert alert-info mt-4 mb-0">
                  <strong>ℹ️ Como funciona:</strong> Cada caça-palavras é gerado 
                  dinamicamente a partir dos {' '}
                  <strong>40.000+ termos</strong> cadastrados na base de dados. 
                  Configure os filtros acima para personalizar seu jogo!
                </div>
              </div>
            </div>
          ) : (
            // ========================================
            // Jogo Iniciado
            // ========================================
            <>
              {/* Timer e Progresso */}
              <div className="alert alert-info d-flex justify-content-between align-items-center mb-4">
                <div>
                  <strong>⏱️ Tempo: {formatarTempo(timer)}</strong>
                </div>
                <div>
                  <strong>🔤 Palavras: {palavrasEncontradas} / {gradeAtual?.total_palavras}</strong>
                </div>
                <div>
                  <button className="btn btn-sm btn-danger" onClick={voltarConfig}>
                    🔄 Nova Configuração
                  </button>
                </div>
              </div>

              {/* Componente Caça-Palavras */}
              {gradeAtual && (
                <CacaPalavras
                  grade={gradeAtual.grade}
                  palavras={gradeAtual.palavras}
                  tema={gradeAtual.tema}
                  dificuldade={gradeAtual.dificuldade}
                  onPalavraEncontrada={handlePalavraEncontrada}
                  onTodasEncontradas={handleTodasEncontradas}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
